import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { subscriptionTabsSelector } from '@mm/state/selectors/subscriptionSelectors';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import sadEmoji from 'assets/emojis/sad.png';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { SettingsRoutes } from '../../../../../routes/settings-routes';
import './CancellationPanel.pcss';
import {
  CancelQuestionary,
  initialPollValues,
  questionaryValidator,
} from './CancelQuestionary/CancelQuestionary';
import {
  ArrowLeftOutlined,
  CloseOutlined,
  ForkOutlined,
  HomeOutlined,
  InfoCircleFilled,
  SendOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { Overlay } from '@mm/components/src/Overlay/Overlay';
import pauseIcon from './icons/pause-icon.png';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { StoreState } from '@mm/state/store-interfaces';
import { Routes } from '../../../../../routes/top-level-routes';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';

const CancelSubscriptonModal = ({
  onClose,
  type,
}: {
  onClose: () => void;
  type: 'pause' | 'confirm';
}) => {
  const isPauseModal = type === 'pause';
  const activeChatbot = useSelector(
    (state: StoreState) => state.chatbot.activeChatbot,
  );
  const history = useHistory();

  const navigateToDashboard = () => {
    if (activeChatbot) {
      history.push(
        BotBuilderRoutes.dashboard.getLink({
          chatbotId: activeChatbot?.id,
        }),
      );
    } else {
      history.push(Routes.chatbotEditor.getLink());
    }
  };

  const onClick = () => {
    if (isPauseModal) {
      onClose();
      history.push(SettingsRoutes.billing.getLink());
      return;
    }

    navigateToDashboard();
  };

  return (
    <div className="pause-subscription-modal-container">
      <CloseOutlined
        className="pause-subscription-modal-close-icon"
        onClick={onClick}
      />
      <img src={isPauseModal ? pauseIcon : sadEmoji} />
      <div className="pause-subscription-modal-title-container">
        {isPauseModal ? 'Pause my Subscription' : 'We are sorry to see you go'}
        {!isPauseModal && (
          <div className="pause-subscription-modal-text">
            You have 7 days to change your mind and reactivate subscription
            without loosing data.
          </div>
        )}
        <div className="pause-subscription-modal-text">
          Please contact support at{' '}
          <a
            className="pause-subscription-modal-link"
            href="mailto:cancellations@customers.ai"
          >
            cancellations@customers.ai
          </a>{' '}
          to {isPauseModal ? 'pause' : 'reactivate'} your subscription
        </div>
      </div>
      <PrimaryButton
        onClick={onClick}
        className="pause-subscription-modal-button"
      >
        {isPauseModal ? 'OK' : 'Go to Dashboard'}
      </PrimaryButton>
    </div>
  );
};

export const CancellationPanel = () => {
  const [stepperIdx, setStepperIdx] = useState(0);
  const [confirmText, setConfirmText] = useState('');
  const [showModal, setShowModal] = useState<'pause' | 'confirm' | undefined>(
    undefined,
  );
  const history = useHistory();
  const subscriptionCRUDActions = useSubscriptionCRUDActions();
  const companyId = useSelector(selectActiveCompanyId);
  const subscriptionTabs = useSelector(subscriptionTabsSelector);
  const { tabId, pageId } = useParams<{ tabId: string; pageId: string }>();

  const activeTab = subscriptionTabs.find(t => t.id === tabId);
  const enhancedFacebookPage = activeTab?.pages?.find(
    p => p.id === Number(pageId),
  );

  const facebookPageId = enhancedFacebookPage?.id;
  const subscriptionId = enhancedFacebookPage?.subscription?.id;
  const isNewBillingSubscription =
    enhancedFacebookPage?.subscription &&
    isBilling2023Subscription(enhancedFacebookPage?.subscription);

  const pollFormik = useFormik({
    initialValues: initialPollValues,
    onSubmit: () => {
      // Note: needed now, since we submt in the next step
    },
    validationSchema: questionaryValidator,
  });

  if (!facebookPageId || !subscriptionId) {
    history.push(SettingsRoutes.billing.getLink());
    return null;
  }

  const handleBack = () => {
    if (stepperIdx === 0) {
      history.push(SettingsRoutes.billing.getLink());
      return;
    }
    setStepperIdx(stepperIdx - 1);
    setConfirmText('');
  };

  const cancelButtonText =
    stepperIdx === 0
      ? 'I want to cancel my subscription'
      : 'Proceed with cancellation';

  const handleCancel = async () => {
    if (stepperIdx === 1) {
      await subscriptionCRUDActions.downgrade(
        companyId,
        subscriptionId,
        {
          companyId,
          facebookPageId,
          leavingReason: 'Replaced with pollResults field',
          additionalInfo: '',
          pollResults: pollFormik.values,
        },
        false,
        isNewBillingSubscription,
        isNewBillingSubscription,
      );
      setShowModal('confirm');
    }
    setStepperIdx(stepperIdx + 1);
  };

  const itemsToLoose = [
    {
      title: 'All your Leads',
      icon: <TeamOutlined />,
    },
    {
      title: 'All your Sends',
      icon: <SendOutlined />,
    },
    {
      title: 'All your Companies',
      icon: <HomeOutlined />,
    },
    {
      title: 'All your Automations',
      icon: <ForkOutlined />,
    },
  ];

  const isCancelDisabled =
    stepperIdx === 1 && !(pollFormik.dirty && pollFormik.isValid);

  return (
    <>
      <div className="cancellation-container">
        <div className="cancellation-container__navigation">
          <div
            className="cancellation-container__navigation-back"
            onClick={handleBack}
          >
            <ArrowLeftOutlined />
            <div>Back to Billing</div>
          </div>
        </div>
        <div className="cancellation-content">
          <div className="cancellation-container__navigation-header">
            Cancel my Subscription
          </div>

          {stepperIdx === 0 && (
            <div className="cancellation__cancel-step-zero">
              <div className="cancellation__cancel-step-zero-top">
                <InfoCircleFilled className="cancellation__cancel-step-zero-top-icon" />
                If you cancel you Subscription you will lose:
              </div>
              <div className="cancellation__cancel-step-zero-bottom">
                {itemsToLoose.map(item => (
                  <div
                    className="cancellation__cancel-step-zero-item"
                    key={item.title}
                  >
                    {item.icon}
                    <div className="cancellation__cancel-step-zero-item-title">
                      {item.title}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stepperIdx === 1 && (
            <FormikProvider value={pollFormik}>
              <CancelQuestionary />
            </FormikProvider>
          )}

          <div
            className={classNames('cancellation-buttons-container', {
              'cancellation-buttons-container-reversed': stepperIdx !== 0,
            })}
          >
            <PrimaryButton
              className={classNames(
                'cancellation__button',
                'cancellation__pause-button',
              )}
              onClick={() => setShowModal('pause')}
            >
              Pause my subscription
            </PrimaryButton>

            <PrimaryButton
              color={
                stepperIdx === 0
                  ? ButtonColor.secondaryBlue
                  : ButtonColor.invertedRed
              }
              className={classNames(
                'cancellation__button',
                'cancellation__cancel-button',
                {
                  'cancellation__cancel-button-red':
                    !isCancelDisabled && stepperIdx !== 0,
                },
              )}
              onClick={handleCancel}
              disabled={isCancelDisabled}
            >
              {cancelButtonText}
            </PrimaryButton>
          </div>
        </div>
      </div>
      {showModal && (
        <Overlay>
          <CancelSubscriptonModal
            onClose={() => setShowModal(undefined)}
            type={showModal}
          />
        </Overlay>
      )}
    </>
  );
};
